import {ControlledBrowserHistory} from '@raison/navigation';
import {Argument} from '@raison/types';
import {
    configureStore,
    createListenerMiddleware,
    ListenerEffectAPI,
    Tuple,
    TypedAddListener,
    TypedStartListening,
    UnknownAction,
} from '@reduxjs/toolkit';
import {Action, combineReducers} from 'redux';
import {ThunkAction, ThunkMiddleware} from 'redux-thunk';
import {modalsReducer} from '~/entities/dialogs';
import {authReducer} from '~/entities/user';
import {env} from '~/environment';
import {translateAndSendError} from '~/processes/app-maintenance';
import assetsReducers from './assets/assetsReducers';
import balancesReducer from './balances/balancesReducer';
import dynamicRateReducers from './dynamicRate/dynamicRateReducers';
import {financesReducer} from './finances/financesReducer';
import {effectsInits} from './listeners';
import {AppServicesRequirements, createStoreServices} from './services';

const {APP_ENV} = env;

// To get most correct types and to avoid circular definitions we must extract types in the same ways redux-toolkit does
declare global {
    type AppStore = ReturnType<typeof createAppStore>;
    type AppModel = ReturnType<AppStore['getState']>;
    type AppDispatch = AppStore['dispatch'];
    type AppServices = ReturnType<typeof createStoreServices>;
    type AppAction = Argument<AppDispatch>;
    type AppSelector<Of, Args extends any[] = []> = (state: AppModel, ...args: Args) => Of;

    type AppThunkAction<TReturn> = ThunkAction<TReturn, AppModel, AppServices, UnknownAction>;
    type AppStartListening = TypedStartListening<AppModel, AppDispatch, AppServices>;
    type AppAddListener = TypedAddListener<AppModel, AppDispatch, AppServices>;
    type AppThunkConfig = {state: AppModel; dispatch: AppDispatch; extra: AppServices};
    type AppEffectAPI = ListenerEffectAPI<AppModel, AppDispatch, AppServices>;
}

export interface AppStoreParams extends AppServicesRequirements {
    key?: number | string;
    history: ControlledBrowserHistory;
}

export function createAppStore({key = '', ...params}: AppStoreParams) {
    const {history} = params;
    const services = createStoreServices(params);

    const {middleware: listenerMiddleware, startListening: _startListening} = createListenerMiddleware({
        extra: services,
        onError: translateAndSendError,
    });
    const startListening = _startListening as AppStartListening;

    const thunkMiddleware = createThunkMiddleware({
        extra: services,
    });

    // not using getDefaultMiddleware because types are wack and
    // listenerMiddleware must be before thunkMiddleware
    const middleware = new Tuple(
        listenerMiddleware,
        thunkMiddleware //
    );

    const reducer = combineReducers({
        history: history.reducer,
        auth: authReducer,
        modals: modalsReducer,

        // must be replaced by other async-state-management solution
        assets: assetsReducers,
        finances: financesReducer,
        dynamicRate: dynamicRateReducers,
        balances: balancesReducer,
    });

    const store = configureStore({
        reducer,
        middleware: _ => middleware,
        devTools: APP_ENV !== 'prod' && {
            name: `RSN${key}`,
            // serialize: getDevToolsSerializeOption(),
        },
    });

    for (const init of effectsInits) {
        init(startListening);
    }

    return store;
}

// decided to move thunk implementation here in case we'll add an error handler
function createThunkMiddleware<TState = any, TActionBase extends Action = UnknownAction, TExtra = undefined>({
    extra,
}: {
    extra: TExtra;
}) {
    const middleware: ThunkMiddleware<TState, TActionBase, TExtra> =
        ({dispatch, getState}) =>
        next =>
        action => {
            if (typeof action === 'function') {
                return action(dispatch, getState, extra);
            }
            return next(action);
        };
    return middleware;
}

// As long as soft version of controlled history is used, this serialization is not needed
// function getDevToolsSerializeOption() {
//     return {
//         replacer: composeJsonTools(
//             HistoryStack.replacer({underKey: 'history'}) //
//         ),
//         reviver: composeJsonTools(HistoryStack.reviver({underKey: 'history'})),
//     };

//     function composeJsonTools(...tools: Array<(key: string, value: unknown) => any>) {
//         return (key: string, value: unknown) => {
//             for (const tool of tools) {
//                 const v = tool(key, value);
//                 if (v !== value) {
//                     return v;
//                 }
//             }
//             return value;
//         };
//     }
// }
